/** biome-ignore-all lint/suspicious/noConsole: <explanation> */
import type { UserAttributes } from '@cobliteam/api-lib';
import { type Session, UserRole } from '@cobliteam/common-types';

import { CookieManager } from '@cobliteam/cookie-manager';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import type { FeatureFlags } from '@cobliteam/hooks/src/useFeatureFlags';
import { Environment } from '@cobliteam/react-environments';
import { PostHog } from './PostHog';
import { captureScreenTraits } from './screenTraits';

// Restrict PostHog usage to admin/manager roles plus cobler (internal Cobli
// testers — see SurveyHost for the always-show bypass). Anyone else never
// loads the SDK and never burns PostHog event/flag quota.
const POSTHOG_ENABLED_ROLES: UserRole[] = [UserRole.ADMINISTRATOR, UserRole.MANAGER, UserRole.COBLER];

// @ts-expect-error biome-ignore lint/correctness/noUndeclaredVariables: <explanation>
declare const analytics: SegmentAnalytics.AnalyticsJS; // Segment global variable initialized on index.html

const env = Environment.getInstance().getEnvironment();
const app_version = window.__DASHBOARD_MONOREPO_VERSION__ || 'unknown';

let hasBeenInitialized = false;
let currentPageTitle = '';

interface Properties {
  [key: string]: any;
}

export class Analytics {
  public static initialize = () => {
    if (hasBeenInitialized) {
      console.warn('Analytics has already been initialized');
      return;
    }

    // Demo / telemetry-disabled builds ship with an empty Segment key —
    // calling analytics.load('') throws, so we no-op instead.
    if (!env.segmentAnalyticsKey) {
      console.info('[Analytics] Segment key missing — analytics disabled.');
      hasBeenInitialized = true;
      return;
    }

    analytics.load(env.segmentAnalyticsKey);
    this.middleware();

    hasBeenInitialized = true;
  };

  public static getCurrentPageTitle = () => currentPageTitle;

  public static setUser = (session: Session, featureFlags: FeatureFlags, userAttributes?: UserAttributes | null) => {
    const { user, fleet } = session;
    const fleetNameLettersAndNumbersOnly = fleet?.name.toLowerCase().replace(/[^a-zA-Z0-9]+/g, '');
    // One read for both identifies, so Segment and PostHog cannot disagree about the same session.
    const screenTraits = captureScreenTraits();

    // Segment is optional (demo / telemetry-disabled builds skip initialize()).
    // Mirror that gate here so identify/group don't run against an unloaded SDK.
    if (hasBeenInitialized && env.segmentAnalyticsKey) {
      analytics.identify(user.id, {
        screen: screenTraits,
        name: user.name,
        role: user.role,
        fleet: fleet.name,
        fleetId: fleet.id,
        email: user.email,
        phone: user.phoneNumber,
        version: app_version,
        featureFlags: featureFlags,
        createdAt: userAttributes?.creationDate,
        numberOfUsers: userAttributes?.numberOfDevices,
        ageInDays: userAttributes?.ageInDays,
      });

      analytics.group(fleet.id, {
        name: fleet.name,
        fleetId: fleet.id,
        numberOfUsers: userAttributes?.numberOfDevices,
        website: `https://www.${fleetNameLettersAndNumbersOnly}.com`,
      });
    }

    // LaunchDarkly kill switch — flip posthogEnabled=false in LD to stop
    // loading the SDK on new sessions without a deploy.
    if (featureFlags.posthogEnabled === false) return;

    // Gate PostHog by role to keep event/flag quota tight. Non-admin/manager
    // roles never load the SDK and never appear as identified persons in
    // PostHog, so they cannot match any survey targeting condition either.
    if (!POSTHOG_ENABLED_ROLES.includes(user.role)) return;

    // enable() lazy-loads the SDK on the first allowed identify; subsequent
    // calls are no-ops.
    PostHog.enable();
    PostHog.identify(user.id, {
      screen: screenTraits,
      name: user.name,
      role: user.role,
      fleet: fleet.name,
      fleetId: fleet.id,
      email: user.email,
      phone: user.phoneNumber,
      version: app_version,
      createdAt: userAttributes?.creationDate,
      numberOfUsers: userAttributes?.numberOfDevices,
      ageInDays: userAttributes?.ageInDays,
    });
  };

  // Re-identifies the current user with updated traits (e.g. a phone number
  // saved after the initial setUser). Segment merges traits, so partial
  // updates are safe. Guarded the same way as setUser so it never runs against
  // an unloaded SDK; PostHog.identify is internally a no-op until enabled.
  public static identify = (userId: string, traits?: Properties) => {
    if (hasBeenInitialized && env.segmentAnalyticsKey) {
      analytics.identify(userId, traits);
    }

    PostHog.identify(userId, traits);
  };

  public static reset = () => {
    if (hasBeenInitialized && env.segmentAnalyticsKey) {
      analytics?.reset?.();
    }
    PostHog.reset();
  };

  public static track = (eventName: string, props?: Properties, debug?: boolean) => {
    const isLocalhost = window.location.href.includes('localhost');
    const isPreview = window.location.href.includes('dashboard.preview.cobli.co');

    const w = window as unknown as { __analyticsEvents?: Array<{ event: string; properties: Properties }> };
    w.__analyticsEvents = w.__analyticsEvents ?? [];
    w.__analyticsEvents.push({ event: eventName, properties: props ?? {} });

    const enabled = debug ? true : !(isLocalhost || isPreview);

    if (enabled) {
      const fleetId = analytics?.group().id ? analytics.group().id() : undefined;
      const hubspotUtk = CookieManager.getCookie('hubspotutk');

      analytics.track(eventName, { ...props, fleetId, hubspotUtk });
      DatadogTrack.action(eventName);
    }

    if (!enabled) {
      console.debug('[Segment] - [DEBUG] Event not Sent', { eventName, props });
    }
  };

  public static pageview = (pageTitle: string, props?: Properties) => {
    const isLocalhost = window.location.href.includes('localhost');
    const isPreview = window.location.href.includes('dashboard.preview.cobli.co');

    currentPageTitle = pageTitle;
    const fleetId = props?.fleetId ? props.fleetId : analytics?.group().id ? analytics.group().id() : undefined;

    if (!(isLocalhost || isPreview)) {
      analytics.page(pageTitle, {
        url: window.location.href,
        path: window.location.pathname,
        search: window.location.search,
        referrer: location.origin,
        fleetId,
        ...props,
      });
    }

    console.debug('[Segment] - Pageview not Sent', { pageTitle, props });
  };

  public static middleware = () => {
    (analytics as any).addSourceMiddleware(({ payload, next }: any) => {
      payload.obj.context = payload.obj.context || {};
      payload.obj.context.app_version = app_version; // Defina a versão do seu app
      next(payload);
    });
  };
}
